// ─── FACTOR COMPUTATION ENGINE ────────────────────────────────────────────────
// Pure quantitative-finance computation. No React, no UI, no API calls, no side
// effects. Takes price/return arrays in and returns factor scores.
//
// All returns are expressed as decimals (0.01 = 1%). Every public function is a
// named export. This is the foundation of the Factor Framework (Phase 1A).
//
// Exception (Phase 4C): checkFundamentalsAvailable at the bottom touches the
// network (a direct authenticated fetch) to probe SEC EDGAR availability.

// ─── INTERNAL MATH HELPERS ────────────────────────────────────────────────────
const TRADING_DAYS = 252;

function isNum(x) {
  return typeof x === "number" && Number.isFinite(x);
}

function mean(arr) {
  if (!arr.length) return null;
  let sum = 0;
  for (const v of arr) sum += v;
  return sum / arr.length;
}

// Population variance (divide by n). For ratios like beta and R² the divisor
// cancels, so population vs. sample makes no difference there.
function variance(arr) {
  if (arr.length < 1) return null;
  const m = mean(arr);
  let sum = 0;
  for (const v of arr) sum += (v - m) * (v - m);
  return sum / arr.length;
}

// Sample standard deviation (divide by n-1) — the standard choice for
// volatility estimated from a sample.
function sampleStd(arr) {
  if (arr.length < 2) return null;
  const m = mean(arr);
  let sum = 0;
  for (const v of arr) sum += (v - m) * (v - m);
  return Math.sqrt(sum / (arr.length - 1));
}

// ─── computeReturns ───────────────────────────────────────────────────────────
// prices: closing prices in chronological order → array of daily returns
// (decimal). return[i] = (price[i] - price[i-1]) / price[i-1].
export function computeReturns(prices) {
  if (!Array.isArray(prices) || prices.length < 2) return [];
  const out = [];
  for (let i = 1; i < prices.length; i++) {
    const prev = prices[i - 1];
    const cur = prices[i];
    if (!isNum(prev) || !isNum(cur) || prev === 0) continue;
    out.push((cur - prev) / prev);
  }
  return out;
}

// ─── computeBeta ──────────────────────────────────────────────────────────────
// OLS of stock returns on market returns.
//   beta  = Cov(stock, market) / Var(market)
//   alpha = mean(stock) - beta * mean(market)
//   residuals[i] = stock[i] - (alpha + beta * market[i])
//   rSquared = 1 - Var(residuals) / Var(stock)
// Arrays are aligned on their most recent overlapping observations.
export function computeBeta(stockReturns, marketReturns) {
  const empty = { beta: null, rSquared: null, alpha: null, residuals: [] };
  if (!Array.isArray(stockReturns) || !Array.isArray(marketReturns)) return empty;

  const n = Math.min(stockReturns.length, marketReturns.length);
  if (n < 2) return empty;

  const s = stockReturns.slice(-n);
  const m = marketReturns.slice(-n);

  const meanS = mean(s);
  const meanM = mean(m);
  const varM = variance(m);
  if (varM === null || varM === 0) return empty; // zero market variance → undefined slope

  let cov = 0;
  for (let i = 0; i < n; i++) cov += (s[i] - meanS) * (m[i] - meanM);
  cov /= n;

  const beta = cov / varM;
  const alpha = meanS - beta * meanM;

  const residuals = s.map((v, i) => v - (alpha + beta * m[i]));

  const varS = variance(s);
  const varResid = variance(residuals);
  const rSquared =
    varS === null || varS === 0 ? null : 1 - varResid / varS;

  return { beta, rSquared, alpha, residuals };
}

// ─── computeMomentum ──────────────────────────────────────────────────────────
// 12-1 momentum: return from the first price to the price ~1 month (21 trading
// days) before the end, excluding the most recent month. Requires ≥ 252 prices.
export function computeMomentum(prices) {
  if (!Array.isArray(prices) || prices.length < TRADING_DAYS) return null;
  const start = prices[0];
  const end = prices[prices.length - 21];
  if (!isNum(start) || !isNum(end) || start === 0) return null;
  return (end - start) / start;
}

// ─── computeVolatility ────────────────────────────────────────────────────────
// Annualized volatility from the trailing 60 daily returns (min 30):
//   std(trailing) * sqrt(252).
export function computeVolatility(returns) {
  if (!Array.isArray(returns)) return null;
  const window = returns.slice(-60);
  if (window.length < 30) return null;
  const std = sampleStd(window);
  if (std === null) return null;
  return std * Math.sqrt(TRADING_DAYS);
}

// ─── computeSize ──────────────────────────────────────────────────────────────
// Percentile rank of marketCap within the universe of market caps.
//   0 = smallest, 1 = largest. null marketCap → null.
export function computeSize(marketCap, universe) {
  if (!isNum(marketCap)) return null;
  if (!Array.isArray(universe)) return null;
  const caps = universe.filter(isNum);
  if (caps.length === 0) return null;
  if (caps.length === 1) return 0.5; // degenerate single-name universe
  let below = 0;
  for (const c of caps) if (c < marketCap) below++;
  const rank = below / (caps.length - 1);
  return Math.max(0, Math.min(1, rank));
}

// ─── computeFactorScores ──────────────────────────────────────────────────────
// Assemble the full factor-score object for a symbol from price data. Any null
// sub-result is preserved as null. value/quality are Sharadar placeholders.
export function computeFactorScores(symbol, prices, marketPrices, marketCap, universe) {
  const stockReturns = computeReturns(prices);
  const marketReturns = computeReturns(marketPrices);

  const { beta, rSquared } = computeBeta(stockReturns, marketReturns);
  const momentum = computeMomentum(prices);
  const volatility = computeVolatility(stockReturns);
  const size = computeSize(marketCap, universe);

  return {
    symbol,
    beta,
    rSquared,
    momentum,
    volatility,
    size,
    value: null, // Sharadar placeholder — see sharadarAvailable()
    quality: null, // Sharadar placeholder — see sharadarAvailable()
    dataSource: "price_derived",
    computedAt: new Date().toISOString(),
  };
}

// ─── FACTOR GROUP METADATA ────────────────────────────────────────────────────
// The factor keys that participate in dominance/grouping, and their human-facing
// group names + descriptions.
const FACTOR_KEYS = ["beta", "momentum", "volatility", "size", "value", "quality"];

const FACTOR_GROUPS = {
  beta: {
    name: "Market Beta / Directional",
    description:
      "Positions whose dominant exposure is broad market direction. They rise and fall largely with the index and offer limited diversification from one another.",
  },
  momentum: {
    name: "High Momentum / Trend",
    description:
      "Positions dominated by trend-following momentum. They tend to move together during momentum regimes and reverse together when momentum breaks.",
  },
  volatility: {
    name: "High Volatility / Risk",
    description:
      "Positions dominated by their own volatility. Elevated idiosyncratic risk means larger swings and shared sensitivity to risk-on / risk-off shifts.",
  },
  size: {
    name: "Size / Capitalization",
    description:
      "Positions dominated by the size factor — their behavior is driven by where they sit on the small-cap to large-cap spectrum.",
  },
  value: {
    name: "Value",
    description:
      "Positions dominated by the value factor (cheap relative to fundamentals). Requires fundamental data.",
  },
  quality: {
    name: "Quality",
    description:
      "Positions dominated by the quality factor (profitability / stability). Requires fundamental data.",
  },
};

// Highest-absolute-exposure factor for a single score object → key or null.
function dominantFactorOf(scores) {
  if (!scores) return null;
  let best = null;
  let bestAbs = -1;
  for (const k of FACTOR_KEYS) {
    const v = scores[k];
    if (!isNum(v)) continue;
    const a = Math.abs(v);
    if (a > bestAbs) {
      bestAbs = a;
      best = k;
    }
  }
  return best;
}

// ─── computePortfolioExposures ────────────────────────────────────────────────
// Weight-average each factor across holdings. Holdings without a factorScores
// entry are skipped; per factor, weights are re-normalized over the holdings
// that have a non-null value for that factor.
export function computePortfolioExposures(holdings, factorScores) {
  const factors = ["beta", "momentum", "volatility", "size"];
  const acc = {};
  const wsum = {};
  for (const f of factors) {
    acc[f] = 0;
    wsum[f] = 0;
  }

  if (Array.isArray(holdings)) {
    for (const h of holdings) {
      if (!h || !isNum(h.weight)) continue;
      const scores = factorScores?.[h.symbol];
      if (!scores) continue; // skip holdings we have no scores for
      for (const f of factors) {
        const v = scores[f];
        if (!isNum(v)) continue;
        acc[f] += h.weight * v;
        wsum[f] += h.weight;
      }
    }
  }

  const exposures = {};
  for (const f of factors) {
    exposures[f] = wsum[f] > 0 ? acc[f] / wsum[f] : null;
  }
  exposures.value = null;
  exposures.quality = null;
  exposures.dominantFactor = dominantFactorOf(exposures);
  return exposures;
}

// ─── countIndependentPositions ────────────────────────────────────────────────
// Group holdings by their dominant factor. Each group is one "independent
// position" — holdings sharing a dominant factor are not truly independent bets.
export function countIndependentPositions(holdings, factorScores) {
  const list = Array.isArray(holdings) ? holdings : [];
  const buckets = new Map(); // factorKey (or "unclassified") → { holdings, weight }

  for (const h of list) {
    if (!h || !h.symbol) continue;
    const dom = dominantFactorOf(factorScores?.[h.symbol]) || "unclassified";
    if (!buckets.has(dom)) buckets.set(dom, { holdings: [], weight: 0 });
    const b = buckets.get(dom);
    b.holdings.push(h.symbol);
    if (isNum(h.weight)) b.weight += h.weight;
  }

  const groups = [];
  for (const [factorKey, b] of buckets.entries()) {
    const meta =
      FACTOR_GROUPS[factorKey] || {
        name: "Unclassified",
        description:
          "Holdings without computed factor scores yet. They are counted but not attributed to a factor group.",
      };
    groups.push({
      name: meta.name,
      dominantFactor: factorKey,
      holdings: b.holdings,
      combinedWeight: b.weight,
      description: meta.description,
    });
  }

  // Largest combined weight first — most concentrated exposure at the top.
  groups.sort((a, b) => b.combinedWeight - a.combinedWeight);

  return {
    totalHoldings: list.length,
    independentPositions: groups.length,
    groups,
  };
}

// ─── computeResidual ──────────────────────────────────────────────────────────
// Idiosyncratic (stock-specific) return: residual[i] = stock[i] - (alpha + beta*market[i]).
export function computeResidual(stockReturns, beta, alpha, marketReturns) {
  if (!Array.isArray(stockReturns) || !Array.isArray(marketReturns)) return [];
  if (!isNum(beta) || !isNum(alpha)) return [];
  const n = Math.min(stockReturns.length, marketReturns.length);
  if (n < 1) return [];
  const s = stockReturns.slice(-n);
  const m = marketReturns.slice(-n);
  return s.map((v, i) => v - (alpha + beta * m[i]));
}

// ─── estimatePairwiseCorrelation ──────────────────────────────────────────────
// Pearson correlation coefficient in [-1, 1]. null if too few observations,
// 0 if either series has zero variance (no linear relationship).
export function estimatePairwiseCorrelation(returnsA, returnsB) {
  if (!Array.isArray(returnsA) || !Array.isArray(returnsB)) return null;
  const n = Math.min(returnsA.length, returnsB.length);
  if (n < 2) return null;
  const a = returnsA.slice(-n);
  const b = returnsB.slice(-n);
  const ma = mean(a);
  const mb = mean(b);
  let cov = 0;
  let va = 0;
  let vb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - ma;
    const db = b[i] - mb;
    cov += da * db;
    va += da * da;
    vb += db * db;
  }
  const denom = Math.sqrt(va * vb);
  if (denom === 0) return 0;
  const corr = cov / denom;
  return Math.max(-1, Math.min(1, corr));
}

// ─── sharadarAvailable ────────────────────────────────────────────────────────
// Fundamental-data gate. Always false until Sharadar is integrated (Phase 4),
// at which point this checks for a valid Sharadar key and the value/quality
// fields in computeFactorScores get populated. UI uses this to decide whether
// to show value/quality scores or an "Available with fundamental data" note.
export function sharadarAvailable() {
  return false;
}

// ─── FUNDAMENTAL DATA (Phase 4C — SEC EDGAR) ──────────────────────────────────
// value/quality factor scores derived from SEC EDGAR ratios (P/B, P/E, ROE, D/E)
// via cross-sectional percentile ranking across the watchlist universe.

// Cached availability result: null = not yet checked, else the boolean.
let _fundamentalsAvailable = null;

// Synchronous gate — returns the cached availability, or false if not yet checked.
export function fundamentalsAvailable() {
  return _fundamentalsAvailable == null ? false : _fundamentalsAvailable;
}

// Async one-time availability probe against the EDGAR endpoint. Never throws.
export async function checkFundamentalsAvailable() {
  try {
    const backendUrl =
      (typeof window !== "undefined" && window.QUANT_SIGNAL_BACKEND) ||
      import.meta.env.VITE_BACKEND_URL;
    if (!backendUrl) {
      _fundamentalsAvailable = false;
      return false;
    }
    // Direct authenticated call — bypass backendFetch (which reads
    // window.QUANT_SIGNAL_BACKEND, which may not be assigned in module scope).
    const response = await fetch(backendUrl + "/api/factors/edgar/AAPL?price=213", {
      headers: {
        "Authorization": "Bearer " + localStorage.getItem("qs_auth_token"),
        "Content-Type": "application/json",
      },
    });
    const data = await response.json();
    _fundamentalsAvailable = data.available === true;
    return _fundamentalsAvailable;
  } catch {
    _fundamentalsAvailable = false;
    return false;
  }
}

// Fraction of the universe strictly less than `value` (count / length).
function percentileRank(value, universe) {
  const vals = (universe || []).filter(isNum);
  if (!vals.length) return 0;
  let below = 0;
  for (const v of vals) if (v < value) below++;
  return below / vals.length;
}

// Value factor score 0-1. Higher = cheaper (more value): low P/B and low P/E.
export function computeValueFactor(pb, pe, universe_pb, universe_pe) {
  const pbScore = isNum(pb) ? 1 - percentileRank(pb, universe_pb) : null;
  const peScore = isNum(pe) ? 1 - percentileRank(pe, universe_pe) : null;
  if (pbScore !== null && peScore !== null) return pbScore * 0.5 + peScore * 0.5;
  if (pbScore !== null) return pbScore;
  if (peScore !== null) return peScore;
  return null;
}

// Quality factor score 0-1. Higher = higher quality: high ROE, low D/E.
export function computeQualityFactor(roe, de, universe_roe, universe_de) {
  // Cap extreme ROE (negative-equity / buyback-compressed equity, e.g. AAPL) at 5.0.
  const roeCapped = isNum(roe) ? Math.min(roe, 5.0) : null;
  const roeScore = roeCapped !== null ? percentileRank(roeCapped, universe_roe) : null;
  const deScore = isNum(de) ? 1 - percentileRank(de, universe_de) : null;
  if (roeScore !== null && deScore !== null) return roeScore * 0.6 + deScore * 0.4;
  if (roeScore !== null) return roeScore;
  if (deScore !== null) return deScore;
  return null;
}
